import { useState, useEffect } from 'react'
import { Spinner } from '@fluentui/react'
import { Section, RadioToggle } from '../components'
import { useAppState } from '../hooks'
import {
  dollars,
  isServiceableAddress,
  StreetAddress,
  ITenant,
  Incentive,
  LoanCalculations,
  EquipmentType,
  AmortizationSchedule,
  LoanOptions as LoanOptsFilter,
  IEquipmentOption,
  Adders,
  isCCEFAuthorized,
} from '@oneethos/shared'
import { AddressInputs, TextInput } from '../components/forms'
import { FaBuilding, FaHandHoldingHeart, FaHouseChimney } from 'react-icons/fa6'
import { Equipments, LoanOptions, SystemDetailInputs } from '../components'
import { CcefAlert } from './ccef-alert'

type PaymentCalculatorProps = {
  formData?: any
  isResidential: boolean
  onChange?: (formData: any) => void
  showCalculation?: boolean
  onShowCalculation?: () => void
  financingTenant?: ITenant
  incentive?: Incentive
  loadingCalculation?: boolean
  interestRate?: number
  loanTermMonths?: number
  amortization?: AmortizationSchedule
  amortizationAutoPay?: AmortizationSchedule
  amortizationNoAutoPay?: AmortizationSchedule
  selectedEquipment?: IEquipmentOption | null
}

export const PaymentCalculator = ({
  formData,
  isResidential,
  onChange,
  onShowCalculation,
  financingTenant,
  incentive,
  loadingCalculation,
}: PaymentCalculatorProps) => {
  const {
    registration: { installer },
    tenant: { config }
  } = useAppState()

  const { products } = config
  const [shouldShowValidation, setShouldShowValidation] = useState(false)
  const [selectedEquipment, setSelectedEquipment] = useState<Partial<IEquipmentOption> | null>(null)
  const [ccefAlertDismissed, setCcefAlertDismissed] = useState(false)

  const isOrgTeslaCertified = installer?.org?.isTeslaCertified
  const installerOrg = installer?.org
  const equipmentTypeForFormData = selectedEquipment?.id

  const shouldShowCCEFMessage = installerOrg ? isCCEFAuthorized(
    installerOrg,
    formData.installationAddress
  ) : false

  const displayCCEFAlert = shouldShowCCEFMessage && !ccefAlertDismissed

  useEffect(() => {
    setCcefAlertDismissed(false)
  }, [shouldShowCCEFMessage])

  const isStorageOnly = equipmentTypeForFormData === EquipmentType.StorageOnly
  const isAddressValid = StreetAddress.isValid(formData.installationAddress)

  const handleEquipmentSelect = (option: IEquipmentOption) => {
    setSelectedEquipment(option)
  }

  useEffect(() => {
    const hasRequiredFields = isStorageOnly
      ? formData.installationAddress && formData.storageQty
      : formData.installationAddress && formData.solarCost && formData.systemSizeKW

    setShouldShowValidation(!!hasRequiredFields)
  }, [formData.installationAddress, formData.solarCost, formData.systemSizeKW, formData.storageQty, isStorageOnly])

  useEffect(() => {
    handleChange({
      projectType: formData.projectType,
    })
  }, [formData.projectType])

  useEffect(() => {
    handleChange({
      equipmentType: equipmentTypeForFormData,
      projectType: formData.projectType,
      installationAddress: formData.installationAddress,
    })

    setShouldShowValidation(false)
  }, [equipmentTypeForFormData])

  useEffect(() => {
    const initialSelectedEquipment = isResidential && isOrgTeslaCertified ?
      { id: EquipmentType.TeslaRoof, isTesla: true } :
      { id: EquipmentType.StandardSolar, isTesla: false }

    setSelectedEquipment(initialSelectedEquipment)
  }, [isResidential, isOrgTeslaCertified])

  const handleChange = (nextData) => {
    const adders = new Adders(nextData.adders).filterNonZero().getList()

    // nextData.adders has { amount: string } which appears to be needed for the form,
    // but it's not valid for LoanCalculations.calcSystemCost

    onChange({
      ...nextData,
      systemCost: dollars(LoanCalculations.calcSystemCost({ ...nextData, adders })),
      loanAmount: dollars(LoanCalculations.calcLoanAmount({ ...nextData, adders })),
      equipmentType: equipmentTypeForFormData
    })
  }

  const { state, country } = (formData.installationAddress || {})

  return (
    <Section>
      <div className="section-title">
        <h2 className="margin-bottom-0">Loan Monthly Payment Calculator</h2>
      </div>
      <div className="form-group">
        <label className="my-2 mb-3 form-label">Select Project Type</label>
        <RadioToggle
          value={formData.projectType}
          onChange={v => handleChange({ ...formData, projectType: v })}
          options={[
            { label: 'Residential', value: 'residential', Icon: FaHouseChimney },
            { label: 'Commercial', value: 'commercial', Icon: FaBuilding },
            { label: 'Non-Profit', value: 'non-profit', Icon: FaHandHoldingHeart },
          ]}
        />
      </div>

      {!formData.projectType ? null : <>
        {isResidential ? null : <div className="form-group">
          <label className="form-label">Business or Non-Profit Name</label>
          <TextInput
            value={formData.businessName}
            onChange={val => handleChange({ ...formData, businessName: val })}
          />
        </div>}

        <AddressInputs
          address={formData.installationAddress || {}}
          label="Address where solar panels will be installed*"
          onChange={address => handleChange({
            ...formData,
            installationAddress: address,
          })}
        />

        {
          (
            isResidential &&
            StreetAddress.isValid(formData.installationAddress) &&
            !isServiceableAddress(state, country, installerOrg)
          ) ? (
            <div className="alert alert-danger">
              <b>Warning:</b> the consumer will be unlikely to complete the application for this
              address as it is outside the supported area. For more information, please contact
              your account representative.
            </div>) : null
        }
        {!isStorageOnly && formData.installationAddress?.state === 'NV' ? (
          <div className="alert alert-warning">
            Per Nevada law SB 379, all solar loans require 100% funding at Permission To
            Operate (PTO) and are not eligible for other funding schedules.
          </div>
        ) : null}
        {displayCCEFAlert && (
          <CcefAlert
            onDismiss={() => setCcefAlertDismissed(true)}
            onPlatformLinkClick={() => handleChange({
              ...formData,
              installationAddress: {},
            })}
          />
        )}
        {!displayCCEFAlert && (
          <>
            <div>
              <label className="mb-2 form-label">Select an equipment type</label>
              <Equipments
                onChange={handleEquipmentSelect}
                isOrgTeslaCertified={isOrgTeslaCertified}
                isResidential={isResidential}
              />
            </div>
            <h4>System Details</h4>
            <SystemDetailInputs
              formData={formData}
              handleChange={handleChange}
              selectedEquipment={selectedEquipment}
              isResidential={isResidential}
              showValidation={shouldShowValidation}
              actionButtons={({ isValid }) => (
                financingTenant ? null : <div style={{ textAlign: 'center' }}>
                  {loadingCalculation ?
                    <Spinner /> :
                    <>
                      <button
                        type="button"
                        className="btn btn-primary"
                        disabled={!isValid || !isAddressValid}
                        onClick={() => onShowCalculation()}
                      >Calculate Payment</button>
                    </>
                  }
                </div>
              )}
            />
            {financingTenant ? <>
              <LoanOptions
                label="Your customer will have the option to select loan term during the application process."
                loanOptions={LoanOptsFilter.for(
                  formData,
                  financingTenant.products?.length ? financingTenant.products : products,
                  financingTenant.feesByProductType,
                )}
                formData={formData}
                financingTenant={financingTenant}
                incentive={incentive}
                showSavings={true}
              />
            </> : null}
          </>
        )}
      </>}
    </Section>
  )
}

export default PaymentCalculator
